import React from 'react';
import { Alert, Badge, Button, Group, Loader, SimpleGrid, Stack, Title } from '@mantine/core';
import { IconArrowLeft, IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PageContainer } from '../../../components/ui/PageContainer';
import { RadarChart } from '../../../components/review360/RadarChart';
import { GapTable } from '../../../components/review360/GapTable';
import { JohariWindow } from '../../../components/review360/JohariWindow';
import { DistributionChart } from '../../../components/review360/DistributionChart';
import { TrendChart } from '../../../components/review360/TrendChart';
import { OpenEndedComments } from '../../../components/review360/OpenEndedComments';
import { useReview360Report, useReview360Trend, useReview360Cycle } from '../../../hooks/useApi';
import { ApiError } from '../../../utils/api';

export const Review360ReportPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, subjectId } = useParams<{ id: string; subjectId: string }>();

  const { data: cycle } = useReview360Cycle(id ?? '');
  const {
    data: report,
    isLoading: isReportLoading,
    isError: isReportError,
    error: reportError,
  } = useReview360Report(id ?? '', subjectId ?? '');
  const { data: trend = [] } = useReview360Trend(subjectId ?? '');

  const subjectName = cycle?.subjects?.find((s) => s._id === subjectId)?.name ?? '';

  if (isReportLoading) {
    return (
      <PageContainer>
        <Group justify="center" py="xl">
          <Loader size="md" />
        </Group>
      </PageContainer>
    );
  }

  if (isReportError || !report) {
    return (
      <PageContainer>
        <Stack gap="md">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(`/review-360/${id}`)}
            size="sm"
            style={{ alignSelf: 'flex-start' }}
          >
            {t('review360.backToList')}
          </Button>
          {reportError instanceof ApiError && reportError.status === 409 ? (
            // Цикл ещё идёт — это ожидаемое состояние, а не сбой.
            <Alert icon={<IconInfoCircle size={16} />} color="blue">
              {t('review360.reportNotReady')}
            </Alert>
          ) : (
            <Alert icon={<IconAlertCircle size={16} />} color="red" title={t('common.error')}>
              {reportError instanceof ApiError && reportError.status === 403
                ? t('errors.accessDenied')
                : t('common.error')}
            </Alert>
          )}
        </Stack>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Stack gap="lg">
        {/* Header */}
        <Group gap="sm" align="center">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(`/review-360/${id}`)}
            size="sm"
          >
            {t('review360.backToList')}
          </Button>
          <Title order={2}>
            {t('review360.reportTitle')}: {subjectName}
          </Title>
          {cycle && (
            <Badge variant="light" size="lg">
              {cycle.title}
            </Badge>
          )}
        </Group>

        {/* Radar Chart */}
        <RadarChart scores={report.competencyScores} />

        {/* Gap Table + Johari Window side by side on desktop */}
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          <GapTable scores={report.competencyScores} />
          <JohariWindow scores={report.competencyScores} />
        </SimpleGrid>

        {/* Distribution */}
        <DistributionChart distributions={report.distributions} />

        {/* Trend */}
        <TrendChart trend={trend} />

        {/* Open-Ended Comments */}
        {report.hasHiddenComments && (
          <Alert icon={<IconInfoCircle size={16} />} color="gray">
            {t('review360.commentsHidden')}
          </Alert>
        )}
        <OpenEndedComments responses={report.openEndedResponses} />
      </Stack>
    </PageContainer>
  );
};
